import type { Edge, Node } from '@xyflow/react'
import type {
  GraphNodeData,
  InlineField,
  JsonArray,
  JsonObject,
  JsonScalar,
  JsonValue,
  PointerField,
  RefInfo,
  ScalarType,
} from '../types'
import { hueFromKey } from './colorFromKey'
import { isRefObject, parsePointer, resolvePointer } from './jsonPointer'

const ROOT_LABEL = 'root'

export type GraphBuild = {
  nodes: Node<GraphNodeData>[]
  edges: Edge[]
}

type ParentEdgeSrc = {
  nodeId: string
  handleId: string
  hue: number
} | null

type DeferredRefEdge = {
  targetPointer: string
  containingNodeId: string
  fieldKey: string
  hue: number
}

export function jsonToGraph(root: JsonValue, rootLabel = ROOT_LABEL): GraphBuild {
  const nodes: Node<GraphNodeData>[] = []
  const edges: Edge[] = []
  const refEdges: DeferredRefEdge[] = []

  let rootValue: JsonValue = root
  if (!isContainer(root) && !isRefObject(root)) {
    rootValue = { value: root }
  }

  if (Array.isArray(rootValue)) {
    buildArrayNode(rootValue, [], rootLabel, rootLabel, null)
  } else if (isRefObject(rootValue)) {
    // A bare $ref as the root — wrap in a synthetic object for coherence
    buildObjectNode({ value: rootValue }, [], rootLabel, rootLabel, null)
  } else {
    buildObjectNode(rootValue as JsonObject, [], rootLabel, rootLabel, null)
  }

  // Resolve deferred ref edges now that all nodes exist
  const nodeIds = new Set(nodes.map((n) => n.id))
  for (const r of refEdges) {
    const src = resolveSourceNodeId(r.targetPointer, nodeIds)
    if (!src) continue
    edges.push({
      id: `ref:${src}->${r.containingNodeId}:${r.fieldKey}`,
      source: src,
      sourceHandle: src.endsWith(':out') ? src : `${src}:out`,
      target: r.containingNodeId,
      targetHandle: 'in',
      type: 'smoothstep',
      style: {
        stroke: `hsl(${r.hue} 50% 55%)`,
        strokeWidth: 1,
        strokeDasharray: '4 4',
      },
      data: { isRef: true, fieldKey: r.fieldKey },
    })
  }

  // Also rewrite ref fields with the resolved source node id for the renderer
  for (const n of nodes) {
    for (const f of n.data.inlineFields) {
      if (f.ref) f.ref.sourceNodeId = resolveSourceNodeId(f.ref.pointer, nodeIds)
    }
    for (const p of n.data.pointerFields) {
      if (p.ref) p.ref.sourceNodeId = resolveSourceNodeId(p.ref.pointer, nodeIds)
    }
  }

  function buildObjectNode(
    value: JsonObject,
    path: (string | number)[],
    label: string,
    hueKey: string,
    parentSrc: ParentEdgeSrc,
  ): string {
    const id = pathToId(path)
    const inlineFields: InlineField[] = []
    const pointerFields: PointerField[] = []

    for (const [k, v] of Object.entries(value)) {
      if (isRefObject(v)) {
        const ref: RefInfo = {
          pointer: v.$ref,
          sourceNodeId: null,
          sourceKey: lastSegment(v.$ref),
        }
        const target = resolvePointer(root, v.$ref)
        const hue = hueFromKey(k)
        if (!isContainer(target)) {
          inlineFields.push({
            key: k,
            value: (target as JsonScalar) ?? null,
            valueType: target === undefined ? 'null' : typeofScalar(target as JsonScalar),
            ref,
          })
        } else {
          const summary = Array.isArray(target)
            ? `[${target.length} ${target.length === 1 ? 'item' : 'items'}]`
            : `{${Object.keys(target).length} ${Object.keys(target).length === 1 ? 'key' : 'keys'}}`
          pointerFields.push({
            key: k,
            childKind: Array.isArray(target) ? 'array' : 'object',
            summary,
            childIds: [],
            ref,
          })
        }
        refEdges.push({
          targetPointer: v.$ref,
          containingNodeId: id,
          fieldKey: k,
          hue,
        })
        continue
      }

      if (!isContainer(v)) {
        inlineFields.push({
          key: k,
          value: v as JsonScalar,
          valueType: typeofScalar(v as JsonScalar),
        })
        continue
      }
      if (Array.isArray(v)) {
        const childIds: string[] = []
        const handleId = `ptr:${k}`
        const hue = hueFromKey(k)
        v.forEach((item, i) => {
          const childPath = [...path, k, i]
          const childLabel = `${k}[${i}]`
          const childId = pathToId(childPath)
          childIds.push(childId)
          const src: ParentEdgeSrc = { nodeId: id, handleId, hue }
          if (isRefObject(item)) {
            // Array item that's a ref — rare, render as a value node placeholder
            const target = resolvePointer(root, item.$ref)
            if (!isContainer(target)) {
              buildRefValueNode(target as JsonScalar, childPath, childLabel, k, src, item.$ref)
            } else {
              buildRefContainerNode(target, childPath, childLabel, k, src, item.$ref)
            }
          } else {
            buildAny(item, childPath, childLabel, k, src)
          }
        })
        pointerFields.push({
          key: k,
          childKind: 'array',
          summary: `[${v.length} ${v.length === 1 ? 'item' : 'items'}]`,
          childIds,
        })
      } else {
        const childPath = [...path, k]
        const childId = pathToId(childPath)
        const handleId = `ptr:${k}`
        const hue = hueFromKey(k)
        const src: ParentEdgeSrc = { nodeId: id, handleId, hue }
        buildObjectNode(v as JsonObject, childPath, k, k, src)
        const keyCount = Object.keys(v as JsonObject).length
        pointerFields.push({
          key: k,
          childKind: 'object',
          summary: `{${keyCount} ${keyCount === 1 ? 'key' : 'keys'}}`,
          childIds: [childId],
        })
      }
    }

    nodes.push({
      id,
      type: 'objectNode',
      position: { x: 0, y: 0 },
      data: {
        path,
        label,
        kind: 'object',
        inlineFields,
        pointerFields,
        hue: hueFromKey(hueKey),
        collapsed: path.length > 0,
        outputPortId: `${id}:out`,
      },
    })

    if (parentSrc) pushEdge(parentSrc, id)
    return id
  }

  function buildArrayNode(
    value: JsonArray,
    path: (string | number)[],
    label: string,
    hueKey: string,
    parentSrc: ParentEdgeSrc,
  ): string {
    const id = pathToId(path)
    const pointerFields: PointerField[] = []

    const childIds: string[] = []
    const handleId = `ptr:items`
    const hue = hueFromKey(hueKey)
    value.forEach((item, i) => {
      const childPath = [...path, i]
      const childLabel = `${label}[${i}]`
      const childId = pathToId(childPath)
      childIds.push(childId)
      const src: ParentEdgeSrc = { nodeId: id, handleId, hue }
      if (isRefObject(item)) {
        const target = resolvePointer(root, item.$ref)
        if (!isContainer(target)) {
          buildRefValueNode(target as JsonScalar, childPath, childLabel, hueKey, src, item.$ref)
        } else {
          buildRefContainerNode(target, childPath, childLabel, hueKey, src, item.$ref)
        }
      } else {
        buildAny(item, childPath, childLabel, hueKey, src)
      }
    })
    pointerFields.push({
      key: 'items',
      childKind: 'array',
      summary: `[${value.length} ${value.length === 1 ? 'item' : 'items'}]`,
      childIds,
    })

    nodes.push({
      id,
      type: 'objectNode',
      position: { x: 0, y: 0 },
      data: {
        path,
        label,
        kind: 'array',
        inlineFields: [],
        pointerFields,
        hue: hueFromKey(hueKey),
        collapsed: path.length > 0,
        outputPortId: `${id}:out`,
      },
    })

    if (parentSrc) pushEdge(parentSrc, id)
    return id
  }

  function buildValueNode(
    scalar: JsonScalar,
    path: (string | number)[],
    label: string,
    hueKey: string,
    parentSrc: ParentEdgeSrc,
  ): string {
    const id = pathToId(path)
    nodes.push({
      id,
      type: 'objectNode',
      position: { x: 0, y: 0 },
      data: {
        path,
        label,
        kind: 'value',
        inlineFields: [],
        pointerFields: [],
        scalarValue: scalar,
        scalarType: typeofScalar(scalar),
        hue: hueFromKey(hueKey),
        collapsed: path.length > 0,
        outputPortId: `${id}:out`,
      },
    })
    if (parentSrc) pushEdge(parentSrc, id)
    return id
  }

  function buildRefValueNode(
    scalar: JsonScalar,
    path: (string | number)[],
    label: string,
    hueKey: string,
    parentSrc: ParentEdgeSrc,
    pointer: string,
  ): string {
    const id = pathToId(path)
    nodes.push({
      id,
      type: 'objectNode',
      position: { x: 0, y: 0 },
      data: {
        path,
        label,
        kind: 'value',
        inlineFields: [
          {
            key: '$ref',
            value: (scalar as JsonScalar) ?? null,
            valueType:
              scalar === undefined || scalar === null ? 'null' : typeofScalar(scalar as JsonScalar),
            ref: { pointer, sourceNodeId: null, sourceKey: lastSegment(pointer) },
          },
        ],
        pointerFields: [],
        scalarValue: scalar,
        scalarType: typeofScalar(scalar),
        hue: hueFromKey(hueKey),
        collapsed: path.length > 0,
        outputPortId: `${id}:out`,
      },
    })
    if (parentSrc) pushEdge(parentSrc, id)
    refEdges.push({
      targetPointer: pointer,
      containingNodeId: id,
      fieldKey: '$ref',
      hue: hueFromKey(hueKey),
    })
    return id
  }

  function buildRefContainerNode(
    target: JsonObject | JsonArray,
    path: (string | number)[],
    label: string,
    hueKey: string,
    parentSrc: ParentEdgeSrc,
    pointer: string,
  ): string {
    const id = pathToId(path)
    const summary = Array.isArray(target)
      ? `[${target.length} ${target.length === 1 ? 'item' : 'items'}]`
      : `{${Object.keys(target).length} ${Object.keys(target).length === 1 ? 'key' : 'keys'}}`
    nodes.push({
      id,
      type: 'objectNode',
      position: { x: 0, y: 0 },
      data: {
        path,
        label,
        kind: 'object',
        inlineFields: [],
        pointerFields: [
          {
            key: '$ref',
            childKind: Array.isArray(target) ? 'array' : 'object',
            summary,
            childIds: [],
            ref: { pointer, sourceNodeId: null, sourceKey: lastSegment(pointer) },
          },
        ],
        hue: hueFromKey(hueKey),
        collapsed: path.length > 0,
        outputPortId: `${id}:out`,
      },
    })
    if (parentSrc) pushEdge(parentSrc, id)
    refEdges.push({
      targetPointer: pointer,
      containingNodeId: id,
      fieldKey: '$ref',
      hue: hueFromKey(hueKey),
    })
    return id
  }

  function buildAny(
    v: JsonValue,
    path: (string | number)[],
    label: string,
    hueKey: string,
    parentSrc: ParentEdgeSrc,
  ): string {
    if (!isContainer(v)) return buildValueNode(v as JsonScalar, path, label, hueKey, parentSrc)
    if (Array.isArray(v)) return buildArrayNode(v as JsonArray, path, label, hueKey, parentSrc)
    return buildObjectNode(v as JsonObject, path, label, hueKey, parentSrc)
  }

  function pushEdge(src: ParentEdgeSrc, targetId: string) {
    if (!src) return
    edges.push({
      id: `${src.nodeId}:${src.handleId}->${targetId}`,
      source: src.nodeId,
      sourceHandle: src.handleId,
      target: targetId,
      targetHandle: 'in',
      type: 'smoothstep',
      style: { stroke: `hsl(${src.hue} 30% 70%)`, strokeWidth: 1 },
    })
  }

  return { nodes, edges }
}

function isContainer(v: JsonValue | undefined): v is JsonObject | JsonArray {
  return v !== undefined && v !== null && typeof v === 'object'
}

function typeofScalar(v: JsonScalar | undefined): ScalarType {
  if (v === null || v === undefined) return 'null'
  if (typeof v === 'boolean') return 'boolean'
  if (typeof v === 'number') return 'number'
  return 'string'
}

function lastSegment(pointer: string): string | null {
  const tokens = parsePointer(pointer)
  if (!tokens || tokens.length === 0) return null
  return tokens[tokens.length - 1]
}

/**
 * Given a JSON-Pointer, find the deepest ancestor (or exact match) that
 * corresponds to a created graph node.
 */
function resolveSourceNodeId(pointer: string, nodeIds: Set<string>): string | null {
  const tokens = parsePointer(pointer)
  if (tokens === null) return null
  for (let i = tokens.length; i >= 0; i--) {
    const id = pathToId(tokens.slice(0, i))
    if (nodeIds.has(id)) return id
  }
  return null
}

export function pathToId(path: (string | number)[]): string {
  return path.length === 0 ? '$' : '$.' + path.map((p) => String(p)).join('.')
}
